import { z } from "zod";
import { runStateSchema } from "./states";

/** The effort selector on the start-run form, as the StartRun board draws it. */
export const runEfforts = ["low", "medium", "high"] as const;
export const runEffortSchema = z.enum(runEfforts);
export type RunEffort = z.infer<typeof runEffortSchema>;

/** The model tier selector on the start-run form. */
export const runModelTiers = ["standard", "deep-reasoning"] as const;
export const runModelTierSchema = z.enum(runModelTiers);
export type RunModelTier = z.infer<typeof runModelTierSchema>;

/**
 * What started a run. Automated runs record the rule and the redacted event.
 * The event is data, never a secret: signing secrets stay off this object.
 */
export const runOriginSchema = z.object({
  kind: z.enum(["person", "schedule", "trigger", "automation"]),
  scheduleId: z.uuid().nullable().optional(),
  triggerId: z.uuid().nullable().optional(),
  ruleId: z.uuid().nullable().optional(),
  event: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type RunOrigin = z.infer<typeof runOriginSchema>;

/**
 * `POST /api/v1/projects/:id/runs`. Everything frozen when the run starts: the work item, the
 * flow version (pinned when asked), the intent, and the limits. The idempotency key makes a
 * repeat submit return the same run, not a second one.
 */
export const startRunSchema = z.object({
  workItemId: z.uuid().nullable(),
  flowId: z.uuid(),
  flowVersionId: z.uuid(),
  pinFlowVersion: z.boolean(),
  intent: z.string().trim().min(1).max(2000),
  branch: z.string().trim().min(1).max(120),
  issueLink: z.string().trim().max(500).optional(),
  modelTier: runModelTierSchema,
  effort: runEffortSchema,
  creditCap: z.number().int().min(1),
  idempotencyKey: z.string().trim().min(8).max(120),
});
export type StartRun = z.infer<typeof startRunSchema>;

/** One step in the run timeline, as the RunDetail board draws it. */
export const runStepSchema = z.object({
  position: z.number().int().min(1),
  name: z.string().min(1),
  kind: z.string().min(1),
  state: runStateSchema,
  detail: z.string().nullable(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  /** The recipe the engine ran and the tail of its output, already scanned for secrets. */
  recipe: z.string().nullable().optional(),
  output: z.string().nullable().optional(),
});
export type RunStep = z.infer<typeof runStepSchema>;

/** One span in a Tracing Hub waterfall, with safe model and tool accounting. */
export const traceSpanSchema = z.object({
  spanId: z.string().min(1),
  parentSpanId: z.string().nullable(),
  name: z.string().min(1),
  kind: z.string().min(1),
  status: z.string().min(1),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  durationMs: z.number().int().min(0),
  attributes: z.record(z.string(), z.unknown()).default({}),
});
export type TraceSpan = z.infer<typeof traceSpanSchema>;

/** A project-scoped trace snapshot returned by the authenticated console proxy. */
export const traceSnapshotSchema = z.object({
  run: z.record(z.string(), z.unknown()),
  spans: z.array(traceSpanSchema),
  spanCount: z.number().int().min(0),
  totalTokens: z.number().int().min(0),
  costMicroUSD: z.number().int().min(0),
  durationMs: z.number().int().min(0),
});
export type TraceSnapshot = z.infer<typeof traceSnapshotSchema>;

/** Attribute names that carry a prompt or a credential. Accounting fields such as `totalTokens` stay. */
const droppedTraceKeys = new Set([
  "prompt",
  "messages",
  "authorization",
  "apikey",
  "secret",
  "token",
  "password",
  "credential",
  "cookie",
  "setcookie",
]);

/** Strips bearer tokens, key assignments, and well-known credential shapes from one string. */
export function redactSensitiveText(value: string): string {
  return value
    .replace(
      /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g,
      "[redacted]",
    )
    .replace(/bearer\s+\S+/gi, "bearer [redacted]")
    .replace(/(api[-_ ]?key|secret|token|password|authorization)\s*[:=]\s*\S+/gi, "$1: [redacted]")
    .replace(
      /\b(?:sk-(?:live|test)-[A-Za-z0-9]+|xox[baprs]-[A-Za-z0-9-]+|ghp_[A-Za-z0-9]+|github_pat_[A-Za-z0-9_]+)\b/g,
      "[redacted]",
    );
}

function traceKey(key: string): string {
  return key.toLowerCase().replaceAll("_", "").replaceAll("-", "");
}

/**
 * Removes raw prompts and credential values from a trace payload. Model name, token counts, and
 * tool identity stay, because the product shows accounting and never the prompt.
 */
export function redactTraceValue(value: unknown): unknown {
  if (typeof value === "string") return redactSensitiveText(value);
  if (Array.isArray(value)) return value.map((item) => redactTraceValue(item));
  if (!value || typeof value !== "object") return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    const normalised = traceKey(key);
    if (normalised.includes("prompt") || droppedTraceKeys.has(normalised)) continue;
    out[key] = redactTraceValue(child);
  }
  return out;
}

/** The credit split on the run detail: all four certainty kinds, always labelled. */
export const runCreditSplitSchema = z.object({
  confirmed: z.number().int().min(0),
  estimated: z.number().int().min(0),
  reserved: z.number().int().min(0),
  unknown: z.number().int().min(0),
});
export type RunCreditSplit = z.infer<typeof runCreditSplitSchema>;

/**
 * `GET /api/v1/projects/:id/runs/:runId`. The run, its step timeline, and the credit split.
 * An unknown effect stays visible on the run until it is reconciled.
 */
export const runDetailSchema = z.object({
  id: z.uuid(),
  traceId: z.string().min(1).nullable().optional(),
  projectId: z.uuid(),
  state: runStateSchema,
  workItemId: z.uuid().nullable(),
  workItemTitle: z.string().nullable(),
  flowId: z.uuid(),
  flowVersionId: z.uuid(),
  flowVersion: z.string().nullable(),
  pinnedFlowVersion: z.boolean(),
  intent: z.string(),
  branch: z.string(),
  modelTier: runModelTierSchema,
  effort: runEffortSchema,
  creditCap: z.number().int().min(1),
  progress: z.number().int().min(0).max(100),
  currentGate: z.string().nullable(),
  gateExpiresAt: z.iso.datetime().nullable(),
  startedBy: z.string().min(1),
  origin: runOriginSchema.optional(),
  steps: z.array(runStepSchema),
  credits: runCreditSplitSchema,
  /** True when some spend cannot be settled to a numeric amount, even if unknown credits are null. */
  spendUnknown: z.boolean(),
  startedAt: z.iso.datetime(),
});
export type RunDetail = z.infer<typeof runDetailSchema>;

/** `POST .../runs` answers 202 with the run id. Starting shows Pending until confirmed. */
export const startRunResponseSchema = z.object({
  id: z.uuid(),
  state: runStateSchema,
});
export type StartRunResponse = z.infer<typeof startRunResponseSchema>;
